import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Heart } from "lucide-react";
import { PRODUCTS } from "../data/content";
import { Reveal, Tag, SolidButton } from "../components/Shared";
import { useWishlist, ProductCard, QuickViewModal } from "../sections/TheEdit";
import { useConsultation } from "../components/Consultation";

export default function ProductPage() {
  const { id } = useParams();
  const product = PRODUCTS.find((p) => p.id === id) || PRODUCTS[0];
  const related = PRODUCTS.filter((p) => p.id !== product.id).slice(0, 4);
  const [size, setSize] = useState(product.sizes[0]);
  const { wishlist, toggle } = useWishlist();
  const [quickView, setQuickView] = useState(null);
  const { open } = useConsultation();
  const wished = wishlist.includes(product.id);

  return (
    <div className="bg-ink px-5 pb-32 pt-28 sm:px-10 md:pt-36" data-testid="product-page">
      <div className="mx-auto max-w-[1600px]">
        <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-fog" data-testid="product-breadcrumbs">
          <Link to="/" className="transition-colors hover:text-flame" data-testid="crumb-home">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/shop" className="transition-colors hover:text-flame" data-testid="crumb-shop">Shop</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-bone">{product.name}</span>
        </nav>

        <div className="mt-10 grid gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="relative aspect-[3/4] overflow-hidden bg-card">
              <img src={product.image} alt={product.name} className="h-full w-full object-cover" data-testid="product-image" />
              <span className="absolute left-5 top-5 bg-ink/70 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-flame backdrop-blur-md">{product.availability}</span>
            </div>
          </Reveal>

          <div className="flex flex-col">
            <Reveal delay={0.1}>
              <Tag>{product.collection}</Tag>
              <h1 className="mt-5 font-serif text-5xl font-light tracking-tight sm:text-6xl" data-testid="product-name">{product.name}</h1>
              <p className="mt-4 font-mono text-xl text-bone" data-testid="product-price">{product.price}</p>
              <p className="mt-6 max-w-md text-base font-light leading-relaxed text-fog">{product.description}</p>
            </Reveal>

            <Reveal delay={0.2} className="mt-10 space-y-8">
              <div>
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-fog">Size — {size}</p>
                <div className="flex flex-wrap gap-2" data-testid="size-selector">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSize(s)}
                      data-testid={`size-option-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`border px-4 py-2.5 font-mono text-[11px] tracking-widest transition-all duration-300 ${
                        size === s ? "border-flame bg-flame text-ink" : "border-line/15 text-bone/70 hover:border-flame/60"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6 border-t border-line/10 pt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">
                <p>Fabric<br /><span className="mt-1 block text-bone/80 normal-case tracking-normal">{product.fabric}</span></p>
                <p>Colours<br /><span className="mt-1 block text-bone/80 normal-case tracking-normal">{product.colors.join(", ")}</span></p>
                <p>SKU<br /><span className="mt-1 block text-bone/80">{product.sku}</span></p>
                <p>Availability<br /><span className="mt-1 block text-bone/80">{product.availability}</span></p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <SolidButton onClick={open} testId="product-enquire-button">Enquire / Reserve</SolidButton>
                <button
                  type="button"
                  onClick={() => toggle(product.id)}
                  data-testid="product-wishlist-button"
                  className={`flex items-center gap-2 border px-6 py-4 font-mono text-[11px] uppercase tracking-[0.25em] transition-all duration-300 ${
                    wished ? "border-flame bg-flame/10 text-flame" : "border-line/15 text-bone/70 hover:border-flame hover:text-flame"
                  }`}
                >
                  <Heart className="h-4 w-4" fill={wished ? "currentColor" : "none"} /> {wished ? "Wishlisted" : "Wishlist"}
                </button>
              </div>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-fog">Cart & checkout unlock with the full e-commerce launch</p>
            </Reveal>
          </div>
        </div>

        <div className="mt-32">
          <Reveal><Tag>Complete the Look</Tag></Reveal>
          <div className="mt-10 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((p) => (
              <ProductCard key={p.id} p={p} onQuickView={setQuickView} wishlist={wishlist} onToggleWishlist={toggle} />
            ))}
          </div>
        </div>
      </div>
      <QuickViewModal product={quickView} onClose={() => setQuickView(null)} wishlist={wishlist} onToggleWishlist={toggle} />
    </div>
  );
}
